import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './query-keys';
import type { CreateCheckoutSessionRequest, SubscriptionTier } from '@/types/subscription';

/**
 * Query options for user's current subscription
 */
export const subscriptionQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.subscriptions.current(),
    queryFn: async () => {
      const response = await api.getSubscription();
      return response.subscription;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1, // Only retry once on failure
  });

/**
 * Query options for user profile
 */
export const userProfileQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.userProfile.current(),
    queryFn: async () => {
      const response = await api.getUserProfile();
      return response;
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    retry: 1,
  });

/**
 * Hook to fetch user's current subscription
 */
export function useSubscription() {
  return useQuery(subscriptionQueryOptions());
}

/**
 * Hook to fetch user profile
 */
export function useUserProfile() {
  return useQuery(userProfileQueryOptions());
}

/**
 * Hook to check daily usage limit
 * Note: This increments usage, so use sparingly
 */
export function useCheckUsageLimit() {
  return useQuery({
    queryKey: queryKeys.usage.daily(),
    queryFn: async () => {
      const response = await api.checkUsageLimit();
      return response;
    },
    enabled: false, // Don't auto-fetch, manually trigger
    staleTime: 0, // Always fresh
    gcTime: 0, // Don't cache
  });
}

/**
 * Hook to create a Stripe checkout session
 */
export function useCreateCheckoutSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tier: SubscriptionTier) => {
      const request: CreateCheckoutSessionRequest = { tier };
      return api.createCheckoutSession(request);
    },
    onSuccess: () => {
      // Invalidate subscription data after checkout
      queryClient.invalidateQueries({
        queryKey: queryKeys.subscriptions.all,
      });
    },
  });
}

/**
 * Hook to create a Stripe customer portal session
 */
export function useCreatePortalSession() {
  return useMutation({
    mutationFn: () => api.createPortalSession(),
    onSuccess: (response) => {
      // Redirect to Stripe customer portal
      window.location.href = response.url;
    },
  });
}

/**
 * Hook to manually refetch subscription status
 * Useful after webhook events or returning from Stripe
 */
export function useRefetchSubscription() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.subscriptions.current(),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.userProfile.current(),
    });
  };
}

import { useImpersonation } from '@/contexts/ImpersonationContext';

/**
 * Hook to get user's current tier
 * Returns 'foundation' if no subscription found
 */
export function useUserTier() {
  // Try to use impersonation context, but fail gracefully
  let impersonation;
  try {
    impersonation = useImpersonation();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    // Ignore error
  }

  const { data: subscription } = useSubscription();

  if (impersonation?.impersonatedRole) {
    return impersonation.impersonatedRole === 'logged-out'
      ? 'foundation'
      : impersonation.impersonatedRole;
  }

  return subscription?.tier || 'foundation';
}

/**
 * Hook to check if user has an active subscription
 */
export function useHasActiveSubscription() {
  const { data: subscription } = useSubscription();
  return subscription?.status === 'active';
}

/**
 * Hook to get user's saved quiz count
 */
export function useQuizCount() {
  return useQuery({
    queryKey: queryKeys.quizCount.total(),
    queryFn: async () => {
      const response = await api.getQuizCount();
      return response.quizCount;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 1,
  });
}
